import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';

import Loader from '../../components/Loader';
import ToastBodyContainer from '../../components/ToastContainer';
import { connectSocket } from '../../redux/basic.slice';
import { fetchInitData } from '../../redux/init.thunks';
import type { RootState } from '../../redux/store';
import AgentConversationPanel from '../AgentConversationPanel';

import Sidebar from './Sidebar';
import TopHeader from './TopHeader';

const Layout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const loader = useSelector((state: RootState) => state.auth.loader);
    const isInitLoaded = useSelector((state: RootState) => state.basic.isInitLoaded);
    const isAuthVerified = useSelector((state: RootState) => state.basic.isAuthVerified);
    const pathsToMatch = [ROUTES.SETUP_ACCOUNT];

    const isAgentPanelOpen = useSelector((state: any) => state.agentConversationPanel?.isOpen ?? false);
    const isAgentPanelDragging = useSelector((state: any) => state.agentConversationPanel?.isDragging ?? false);

    const [isPageReady, setIsPageReady] = useState(false);

    // Fires once per signed-in user, NOT on every render/navigation.
    // Deliberately keyed on currentUser?.id (a primitive) and not on the currentUser object:
    // Settings, AccountSetupStatus, QuoteDetail, OrderDetailsWidgets and others all dispatch
    // setCurrentUser({...}) with a fresh object, so an object-identity dep re-ran this on
    // practically every page. The ref additionally covers StrictMode's double-invoke and the
    // in-flight window (isInitLoaded only flips once the response is bound).
    const initLoadedForUserRef = useRef<string | null>(null);

    // THE single app-boot request. One GET /common/init returns column settings, services,
    // equipment types, handling units, commodities and shippers/consignees, and binds all of it
    // into Redux. Pages and modals read that state — they do not fetch this data themselves.
    useEffect(() => {
        // isAuthVerified gate: on a hard refresh isAuthenticated/currentUser are rehydrated from
        // localStorage before the token has been checked, so without this the init call would race
        // get-user-details and 401 on an expired session.
        if (!isAuthVerified || !isAuthenticated || !currentUser?.id) return;

        const userKey = String(currentUser.id);

        if (initLoadedForUserRef.current === userKey) return;

        initLoadedForUserRef.current = userKey;
        dispatch(fetchInitData() as any);
    }, [isAuthVerified, isAuthenticated, currentUser?.id, dispatch]);

    // Initialize sidebar state based on screen size
    const getInitialSidebarState = () => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 768; // Open on large screens, closed on small screens
        }

        return true; // Default fallback
    };

    const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarState);

    useEffect(() => {
        if (loader) {
            setIsPageReady(false);

            return;
        }

        if (isAuthenticated && !currentUser) {
            setIsPageReady(false);

            return;
        }

        const timer = setTimeout(() => {
            setIsPageReady(true);
        }, 200);

        return () => clearTimeout(timer);
    }, [loader, isAuthenticated, currentUser]);

    useEffect(() => {
        // Runs only when auth/page-ready state changes — NOT on every disconnect.
        // Sails.io handles reconnection internally; triggering connectSocket() on every
        // isSocketConnected=false would race with the auto-reconnect and stack listeners.
        // Also waits on isAuthVerified — no point opening an authenticated socket with a token
        // that get-user-details is about to reject.
        if (isAuthVerified && isAuthenticated && currentUser && isPageReady) {
            const socketTimer = setTimeout(() => {
                dispatch(connectSocket() as any);
            }, 1000);

            return () => clearTimeout(socketTimer);
        }
    }, [isAuthVerified, isAuthenticated, currentUser, isPageReady, dispatch]);

    useEffect(() => {
        if (currentUser && !currentUser?.isSetBasicDetail) {
            navigate(ROUTES.SETUP_ACCOUNT);
        }
    }, [isAuthenticated, currentUser, navigate]);

    const isSmallScreen = () => window.innerWidth < 768;

    useEffect(() => {
        const handleResize = () => {
            const isLargeScreen = window.innerWidth >= 768;

            if (isLargeScreen && !isSidebarOpen) {
                setIsSidebarOpen(true);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isSidebarOpen]);

    // Boot order, in this exact sequence:
    //   1. get-user-details settles        -> isAuthVerified
    //   2. /common/init binds its payload  -> isInitLoaded
    //   3. pages render and may call APIs
    // The isAuthVerified term is scoped to `isAuthenticated` so the login and other public routes
    // (which render through this same Outlet) are never held behind the loader.
    const shouldShowLoader = loader || !isPageReady || (isAuthenticated && !isAuthVerified) || (isAuthenticated && !currentUser) || (isAuthenticated && !!currentUser && !isInitLoaded);

    if (shouldShowLoader) {
        return <Loader />;
    }

    return (
        <div
            id="app-layout"
            className={` flex flex-col w-full ${
                isAuthenticated && !pathsToMatch?.includes(window.location.pathname) ? 'bg-utilityBlack700 ' : 'h-full p-6 bg-utilityGray100 max-sm:p-0 max-sm:bg-utilityBlack'
            }`}
        >
            {isAuthenticated && !pathsToMatch?.includes(window.location.pathname) ? (
                <>
                    <TopHeader isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

                    <div className={`rounded-t-xl flex-1 custom-scrollbar-main overflow-hidden relative w-full flex flex-col ${isSmallScreen() && isSidebarOpen ? 'sidebar-open-overlay' : ''}`}>
                        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
                        <div
                            className={`flex flex-1 flex-col overflow-hidden ${!isAgentPanelDragging ? 'transition-[padding-right] duration-300 ease-in-out' : ''}`}
                            style={{ paddingRight: isAgentPanelOpen ? 'var(--agent-panel-width, 402px)' : 0 }}
                        >
                            <Outlet />
                        </div>
                        <AgentConversationPanel />
                    </div>
                </>
            ) : (
                <Outlet />
            )}
            <ToastBodyContainer />
        </div>
    );
};

export default Layout;
